import { useState } from "react";
import { Phone, Plus } from "lucide-react";
import { toast } from "sonner";
import { BRAND, MENU, EXTRAS_IMAGES, fmt, slug } from "../data/restaurant";
import { useCart } from "../context/CartContext";
import { Reveal } from "./motion/Reveal";
import { BrushUnderline } from "./BrushHeading";

const PILL_LINKS = [
  { id: "cat-appetizers", label: "Appetizers" },
  { id: "cat-burgers", label: "Burgers" },
  { id: "cat-pasta", label: "Pasta" },
  { id: "cat-dippings", label: "Dippings" },
  { id: "cat-drinks", label: "Drinks" },
];

const CATEGORIES = [
  { id: "appetizers", label: "Appetizers", items: MENU.appetizers },
  { id: "burgers", label: "Burgers", items: MENU.burgers },
  { id: "pasta", label: "Pasta", items: MENU.pasta },
  { id: "dippings", label: "Dippings", items: MENU.dippings },
  { id: "drinks", label: "Drinks", items: MENU.drinks },
];

const CAT_IMAGES = {
  dippings: EXTRAS_IMAGES.dippings,
  drinks: EXTRAS_IMAGES.drinks,
};

function MenuCard({ item, category }) {
  const { addItem, setOpen } = useCart();
  const [picked, setPicked] = useState([]);

  const toggleAddon = (a) =>
    setPicked((prev) =>
      prev.some((x) => x.id === a.id) ? prev.filter((x) => x.id !== a.id) : [...prev, a],
    );

  const onAdd = () => {
    const extra = picked.reduce((s, a) => s + a.price, 0);
    const label = picked.map((p) => p.label).join(", ");
    addItem({
      id: `${category}-${slug(item.name)}${picked.length ? `-${picked.map((p) => p.id).join("-")}` : ""}`,
      name: item.name,
      price: item.price + extra,
      category,
      img: item.img || null,
      options: picked.map((p) => p.label),
    });
    toast.success(`${item.name}${label ? ` + ${label}` : ""} added to cart`, {
      action: { label: "View cart", onClick: () => setOpen(true) },
    });
  };

  return (
    <article
      data-testid="order-menu-card"
      className="flex h-full flex-col justify-between gap-4 rounded-2xl border-[3px] border-jet bg-cream p-5 shadow-hard-sm transition-all duration-200 hover:-translate-y-1.5 hover:shadow-hard sm:p-6"
    >
      <div className="flex gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-end">
            <h4 className="font-display text-lg font-extrabold leading-tight text-jet sm:text-xl">{item.name}</h4>
            <span className="dots-leader" aria-hidden="true" />
            <span className="font-display text-lg font-black text-crimson sm:text-xl">
              {fmt(item.price)}
              {item.unit && <span className="ml-1 text-xs font-bold text-jet/50">{item.unit}</span>}
            </span>
          </div>
          {item.desc && <p className="mt-2 text-sm font-medium leading-snug text-jet/70">{item.desc}</p>}
          {(item.deal || item.comesWith || item.unit) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {item.deal && (
                <span className="-rotate-2 rounded-full border-2 border-jet bg-flame px-3 py-0.5 text-[11px] font-extrabold uppercase tracking-widest text-cream">
                  $10 Deal
                </span>
              )}
              {item.comesWith && (
                <span className="rounded-full border-2 border-jet bg-sunshine/70 px-3 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-jet">
                  {item.comesWith}
                </span>
              )}
              {item.unit && (
                <span className="rounded-full border-2 border-jet/30 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-jet/60">
                  {item.unit}
                </span>
              )}
            </div>
          )}
          {item.addons && item.addons.length > 0 && (
            <div className="mt-3 flex flex-col gap-1.5">
              {item.addons.map((a) => (
                <label key={a.id} className="inline-flex cursor-pointer items-center gap-2 text-sm font-bold text-jet">
                  <input
                    type="checkbox"
                    checked={picked.some((x) => x.id === a.id)}
                    onChange={() => toggleAddon(a)}
                    data-testid={`addon-${slug(item.name)}-${a.id}`}
                    className="h-4 w-4 accent-crimson"
                  />
                  {a.label} <span className="text-crimson">+{fmt(a.price)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {item.img && (
          <img
            src={item.img}
            alt={`${item.name} at Pattista`}
            className="h-20 w-20 shrink-0 self-start rounded-2xl border-2 border-jet object-cover shadow-hard-sm sm:h-24 sm:w-24"
          />
        )}
      </div>
      <button
        type="button"
        data-testid={`add-to-cart-${slug(item.name)}`}
        onClick={onAdd}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-jet px-5 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-cream shadow-hard-flame transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson hover:shadow-hard"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add to Cart
      </button>
    </article>
  );
}

export default function OrderingMenu() {
  return (
    <section id="menu" data-testid="menu-section" className="bg-gradient-to-b from-sunshine/40 via-cream to-cream py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="max-w-3xl">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border-2 border-jet bg-cream px-4 py-1 text-xs font-bold uppercase tracking-[0.2em] text-crimson shadow-hard-sm sm:text-sm">
            Menu · Pickup Orders
          </p>
          <h2 className="font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-jet sm:text-5xl">
            Pick your <BrushUnderline>craving</BrushUnderline>
          </h2>
          <p className="mt-4 text-base font-medium text-jet/80">
            {BRAND.priceNote} All prices in Canadian dollars (CAD).
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-2.5" data-testid="menu-category-nav">
          {PILL_LINKS.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              data-testid={`menu-jump-${p.id.replace("cat-", "")}`}
              className="rounded-full border-[3px] border-jet bg-cream px-5 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-jet shadow-hard-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-flame hover:text-cream"
            >
              {p.label}
            </a>
          ))}
        </Reveal>

        {CATEGORIES.map((cat, ci) => (
          <Reveal key={cat.id} delay={0.05} className="pt-12">
            <div id={`cat-${cat.id}`} className="scroll-mt-28">
              {CAT_IMAGES[cat.id] && (
                <div className="mb-6 overflow-hidden rounded-3xl border-[3px] border-jet shadow-hard">
                  <img
                    src={CAT_IMAGES[cat.id].img}
                    alt={CAT_IMAGES[cat.id].alt}
                    className="aspect-[21/9] w-full object-cover"
                  />
                </div>
              )}
              <h3 className="font-display text-2xl font-black uppercase tracking-tight text-crimson sm:text-3xl">
                {cat.label}
              </h3>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {cat.items.map((item) => (
                  <MenuCard key={item.name} item={item} category={cat.id} />
                ))}
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal className="mt-12 flex flex-col items-center gap-3 text-center">
          <p className="text-base font-bold text-jet">Prefer to order by phone?</p>
          <a
            href={BRAND.phoneHref}
            data-testid="menu-call-cta"
            className="inline-flex items-center gap-2 rounded-full border-[3px] border-jet bg-crimson px-8 py-4 font-display text-base font-extrabold uppercase text-cream shadow-hard-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-hard"
          >
            <Phone className="h-5 w-5" aria-hidden="true" />
            Call to Order · {BRAND.phoneDisplay}
          </a>
        </Reveal>
      </div>
    </section>
  );
}
